/**
 * Maps the two outcomes of an ad-hoc notification send to user-facing
 * copy:
 *
 *  1. `resolveResultToast` — the HTTP-200 per-send result. SENT is a
 *     success, every `SKIPPED_*` is a warning (nothing was delivered but
 *     it's not an operator error), FAILED is an error carrying the
 *     provider message.
 *  2. `resolveSendErrorMessage` — a thrown `ApiError` (4xx/5xx/network).
 *     The backend uses a `{ errorCode, message }` envelope for domain
 *     errors (EMPTY_BODY / SMS_NO_PHONE_ON_FILE / USER_NOT_FOUND) and the
 *     standard class-validator `{ message[] }` shape for validation —
 *     this resolver handles both, plus the throttler's 429.
 *
 * Pure + translator-injected so it's unit-testable without a hook.
 */

import { ApiError } from "@/shared/api/http-client";
import type { AdminNotificationResult } from "./notification-types";

export type NotificationToastTone = "success" | "warning" | "error";

export interface ResultToast {
  readonly tone: NotificationToastTone;
  readonly key: string;
  readonly values?: Record<string, unknown>;
}

export function resolveResultToast(result: AdminNotificationResult): ResultToast {
  switch (result.status) {
    case "SENT":
      return {
        tone: "success",
        key: "users.notify.result.sent",
        values: { devices: result.devicesTargeted ?? 0 },
      };
    case "SKIPPED_PREF":
      return { tone: "warning", key: "users.notify.result.skippedPref" };
    case "SKIPPED_SUPPRESSED":
      return { tone: "warning", key: "users.notify.result.skippedSuppressed" };
    case "SKIPPED_PROVIDER_OFF":
      return { tone: "warning", key: "users.notify.result.skippedProviderOff" };
    case "SKIPPED_NO_DEVICES":
      return { tone: "warning", key: "users.notify.result.skippedNoDevices" };
    case "FAILED":
      return {
        tone: "error",
        key: "users.notify.result.failed",
        values: { message: result.errorMessage ?? "" },
      };
  }
}

/** Reads the backend's `{ errorCode }` field off an ApiError body. */
function readErrorCode(err: ApiError): string | null {
  const body = err.body;
  if (!body || typeof body !== "object") return null;
  const code = (body as { errorCode?: unknown }).errorCode;
  return typeof code === "string" ? code : null;
}

export function resolveSendErrorMessage(
  err: unknown,
  t: (key: string, values?: Record<string, unknown>) => string,
): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return t("users.notify.errors.rateLimit");
    if (err.status === 401 || err.status === 403) {
      return t("users.notify.errors.forbidden");
    }

    const code = readErrorCode(err);
    if (code === "SMS_NO_PHONE_ON_FILE") {
      return t("users.notify.errors.smsNoPhone");
    }
    if (code === "EMPTY_BODY") return t("users.notify.errors.emptyBody");
    if (code === "USER_NOT_FOUND" || err.status === 404) {
      return t("users.notify.errors.userNotFound");
    }
  }
  return t("users.notify.errors.generic");
}
